/**
 * Collapse the per-recipient message-trace fan-out into one row per message.
 *
 * Message trace is stored at PER-RECIPIENT grain: one message to three recipients is
 * three rows with identical time, sender, subject and IP, differing only in recipient,
 * size and delivery status. In the timeline that reads as three separate sends, which is
 * how a single SharePoint share notification on IR-20260818-Controller-001 appeared as
 * two indistinguishable "SendMail" rows the analyst could not account for.
 *
 * The rows are NOT discarded. Every constituent id is carried on `_collapsedIds` so
 * dismissal, phishing flags and the reports act on all of them, and so a collapsed row
 * can never quietly stand for fewer rows than it came from.
 *
 * 🔴 DELIVERY STATUS IS PER RECIPIENT AND MUST STAY THAT WAY. Measured over 422,858
 * message groups in one fleet-day: of the 41,206 multi-recipient groups, 18,715 (45.4%)
 * carry MORE THAN ONE distinct status. A collapsed row that showed a single "delivered"
 * would assert delivery to recipients the message never reached, so status travels with
 * each recipient and the row-level `Status` becomes 'mixed' rather than picking a winner.
 *
 * Grouping key, in order of preference:
 *   1. `SilverRecordId` — Microsoft's own message-level trace id. Verified constant
 *      within a group for `messageId`, sender, subject and timestamp; NOT for status.
 *   2. The `EventId` prefix before the first `|`. The runbook's row key is
 *      `{Id}|{recipient}`, so the prefix IS that same id. This fallback exists because
 *      `SilverRecordId` was assigned on the in-memory object but never named in the
 *      output projection, so it is **null on every blob written before v1.152.0** —
 *      without the fallback, collapsing would silently do nothing on every existing case.
 *
 * Deliberately NOT grouped on `MessageId`: it is coarser (356,515 distinct vs 422,858
 * ids in the same day), so the same RFC message id can span several trace records — a
 * message both received and sent, or expanded through a distribution list — and merging
 * those would put an inbound receipt and an outbound send in one row.
 */

/** Largest recipient list rendered inline before the caller should switch to a count. */
export const MT_RECIPIENT_DISPLAY_CAP = 12;

/** Message-level key for a message-trace row, or '' when it cannot be determined. */
export function messageTraceGroupKey(evt) {
  if (!evt) return '';
  if (evt.SilverRecordId) return String(evt.SilverRecordId);
  const id = evt.EventId ? String(evt.EventId) : '';
  const bar = id.indexOf('|');
  // No separator means the runbook fell back to a bare GUID for a row with no id, which
  // is already unique -- returning it groups the row with itself, which is correct.
  return bar > 0 ? id.slice(0, bar) : id;
}

/**
 * @param events message-trace events (already `_eventType`-tagged or raw)
 * @returns one row per message, in the input's order of first appearance
 */
export function collapseMessageTrace(events) {
  const list = Array.isArray(events) ? events.filter(Boolean) : [];
  const groups = new Map();
  for (const evt of list) {
    const key = messageTraceGroupKey(evt);
    if (!key) { groups.set(`__ungrouped_${groups.size}`, [evt]); continue; }
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(evt);
  }

  const out = [];
  for (const members of groups.values()) {
    if (members.length === 1) { out.push({ ...members[0], _collapsedIds: [members[0].EventId] }); continue; }

    // The primary is chosen by EventId, not by recipient: it decides which row's
    // identity the collapsed row inherits, so it must not move between renders.
    const ordered = [...members].sort((a, b) => String(a.EventId).localeCompare(String(b.EventId)));
    const primary = ordered[0];

    const recipients = ordered.map(m => ({
      address: m.RecipientAddress || '',
      status: m.Status || '',
      size: m.Size ?? null,
      eventId: m.EventId,
    }));

    const statusCounts = {};
    for (const r of recipients) {
      const key = r.status || 'unknown';
      statusCounts[key] = (statusCounts[key] || 0) + 1;
    }
    const distinctStatuses = Object.keys(statusCounts);
    const statusMixed = distinctStatuses.length > 1;

    // Microsoft's own count of recipients on the send. The case query returns only the
    // trace rows matching this user's addresses, so the fan-out we hold can be a strict
    // subset -- and a partial list presented as the whole thing is the failure this
    // field exists to prevent. Null on an unmatched send or a pre-v1.152.0 blob, in
    // which case the caller must not claim completeness either way.
    const declared = ordered.map(m => m.SendRecipientCount).find(v => typeof v === 'number' && v > 0) ?? null;

    out.push({
      ...primary,
      _collapsed: true,
      _collapsedIds: ordered.map(m => m.EventId),
      _recipients: recipients,
      _recipientCount: recipients.length,
      _declaredRecipientCount: declared,
      _recipientsPartial: typeof declared === 'number' && declared > recipients.length,
      _statusCounts: statusCounts,
      _statusMixed: statusMixed,
      Status: statusMixed ? 'mixed' : (primary.Status || ''),
      // Sizes differ per recipient and summing them would invent a figure Microsoft
      // never recorded, so the collapsed row keeps the primary's own size.
      RecipientAddress: primary.RecipientAddress || '',
    });
  }
  return out;
}

/**
 * One-line recipient summary for a collapsed row: "a@x.com, b@y.com" or, past the cap,
 * "a@x.com, b@y.com +9 more". States the true total, never a truncated impression of it.
 */
export function recipientSummary(evt, cap = MT_RECIPIENT_DISPLAY_CAP) {
  const recips = evt?._recipients;
  if (!Array.isArray(recips) || recips.length === 0) return evt?.RecipientAddress || '';
  const shown = recips.slice(0, cap).map(r => r.address).filter(Boolean);
  const hidden = recips.length - shown.length;
  return hidden > 0 ? `${shown.join(', ')} +${hidden} more` : shown.join(', ');
}

/** "3 recipients, 2 delivered / 1 failed" — or '' for a single-recipient row. */
export function recipientStatusSummary(evt) {
  if (!evt?._collapsed) return '';
  const parts = Object.entries(evt._statusCounts || {})
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([status, n]) => `${n} ${status}`);
  const head = `${evt._recipientCount} recipient${evt._recipientCount === 1 ? '' : 's'}`;
  const partial = evt._recipientsPartial ? ` of ${evt._declaredRecipientCount} on the message` : '';
  return parts.length ? `${head}${partial}, ${parts.join(' / ')}` : `${head}${partial}`;
}

/**
 * Collapse only the message-trace entries inside a mixed, already-tagged event stream,
 * leaving every other event untouched and preserving the stream's order by each
 * message's FIRST appearance.
 *
 * Used by the reports rather than changing `combineAndTagEvents`, which is shared with
 * the Executive Report fact pack: that pack pins a `FACT_SCHEMA_VERSION` and reconciles
 * against the run blob, so silently changing the shape of the stream underneath it is
 * how this feature's nine contract bugs happened.
 *
 * It also corrects a count. The report says "N emails were sent" from the length of this
 * filtered stream, which at per-recipient grain counts one message to three people as
 * three emails.
 */
export function collapseMessageTraceInStream(events) {
  const list = Array.isArray(events) ? events.filter(Boolean) : [];
  const traces = list.filter(e => e._eventType === 'MessageTrace');
  if (traces.length === 0) return list;

  const collapsed = collapseMessageTrace(traces);
  const byPrimaryId = new Map(collapsed.map(c => [c.EventId, c]));
  // Every id the collapsed rows absorbed, so a non-primary constituent is dropped
  // rather than left behind as a duplicate of the row that now represents it.
  const absorbed = new Set();
  for (const c of collapsed) for (const id of (c._collapsedIds || [])) absorbed.add(id);

  const out = [];
  for (const evt of list) {
    if (evt._eventType !== 'MessageTrace') { out.push(evt); continue; }
    if (byPrimaryId.has(evt.EventId)) { out.push(byPrimaryId.get(evt.EventId)); continue; }
    if (absorbed.has(evt.EventId)) continue;
    out.push(evt);
  }
  return out;
}

/**
 * The one-line message-trace description, shared by BOTH copies of
 * `generateDescription` (`eventUtils.js` and `TimelineView.jsx`) so a collapsed row
 * cannot describe itself differently in the timeline than in a report.
 *
 * `reveal` is passed explicitly because the two copies hold the flag with OPPOSITE
 * polarity (`!maskSubjects` vs `revealSubjects`).
 */
export function describeMessageTrace(event, reveal, maskSubjectFn) {
  const direction = event?.Direction || '';
  const raw = event?.Subject || '';
  const subject = raw ? maskSubjectFn(raw, reveal) : 'No subject';
  const verb = direction === 'Sent' ? 'sent to' : 'received from';
  const other = direction === 'Sent'
    ? (event?._collapsed ? recipientSummary(event) : event?.RecipientAddress)
    : event?.SenderAddress;

  // A collapsed row must not print one status: 45.4% of multi-recipient messages have
  // more than one outcome, so it prints the tally instead.
  let status = event?.Status || '';
  if (event?._collapsed && event._statusMixed) {
    status = Object.entries(event._statusCounts || {})
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([st, n]) => `${n} ${st}`)
      .join(' / ');
  }
  const count = event?._collapsed ? ` (${event._recipientCount} recipients${event._recipientsPartial ? ` of ${event._declaredRecipientCount}` : ''})` : '';
  const phishTag = event?.Operation === 'ReceivedPhish' ? ' [PHISH]' : '';
  return `${verb} ${other || 'unknown'}${count}: "${subject}" [${status}]${phishTag}`;
}
